import { createContext, useEffect, useState } from 'react';
import FuseSplashScreen from '@fuse/core/FuseSplashScreen';
import { User, useAuth0 } from '@auth0/auth0-react';
import AppContext from '../AppContext';
import { setAccessToken } from '../configs/axiosConfig';

import useAuthUser, { CustomUserFields } from '../hooks/useAuthUser';
import useUserRoutes from '../hooks/useUserRoutes';
import useUserSignIn from '../hooks/useUserSignIn';
import { ILocalStoreKey } from '../types';
import { tryLinkExist } from '../api/AuthApi';
import { identifyUser } from '../../posthog';

const AuthContext = createContext<(User & CustomUserFields) | undefined>(undefined);
const ALLOW_ANONYMOUS_TO_PAGES = [
  '/privacy',
  '/merchant/resubmit',
  '/merchant/sign-in',
  '/agent/sign-in',
];

function AuthProvider(props) {
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [loading, setLoading] = useState(true);
  const user = useAuthUser();

  const location = window.location.pathname;
  const { handleSignIn, isLoading: signInLoading } = useUserSignIn();

  useEffect(() => {
    const handle = async () => {
      const allowAnonymous = ALLOW_ANONYMOUS_TO_PAGES.includes(location);
      if (isLoading || allowAnonymous) return;

      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        setAccessToken(token);

        // Clear merchant localStorage when user is a processor to prevent
        // stale data from redirecting them to merchant onboarding
        if (user?.role === 'processor') {
          console.log('Removing merchant form data');
          localStorage.removeItem(ILocalStoreKey.merchantFormData);
          localStorage.removeItem(ILocalStoreKey.loginQueryParamsData);
        }

        identifyUser(user);

        if (user?.role === undefined) {
          const { linked } = await tryLinkExist();
          if (linked) window.location.reload();
        }
      } else {
        if (window.location.search) {
          localStorage.setItem(ILocalStoreKey.loginQueryParamsData, window.location.search);
        } else {
          localStorage.removeItem(ILocalStoreKey.loginQueryParamsData);
        }

        await loginWithRedirect({
          appState: { returnTo: location },
        });
      }
      setLoading(false);
    };
    handle().catch(console.error);
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    location,
    isLoading,
    loginWithRedirect,
    user,
    handleSignIn,
  ]);

  const currentRoutes = useUserRoutes({ user });

  if (
    (isLoading || signInLoading || !isAuthenticated || !user || loading) &&
    !ALLOW_ANONYMOUS_TO_PAGES.includes(location)
  ) {
    return <FuseSplashScreen />;
  }

  return (
    <AppContext.Provider
      value={{
        routes: currentRoutes,
        isCompleteOnboarding: user?.onboarding ? user.onboarding : false,
      }}
    >
      <AuthContext.Provider value={user}>{props.children}</AuthContext.Provider>
    </AppContext.Provider>
  );
}
export default AuthProvider;
